/**
 * The Documents header under a project (card 4.3): instead of Personal / This
 * device / Teams it offers "Main" and the project's documentation spaces, with
 * "New space…" / "Manage spaces…". Under All projects / No project it returns
 * `inProject: false` and the sidebar keeps its usual switcher.
 *
 * The tree stays ONE bucket (`bucketScope`): a team project's team, else the
 * user's own one they had picked (cloud or this device). The space is a
 * filter on it (useDocSpaces → documentsProjectFilter).
 */
import { useCallback, useMemo, useState } from 'react';
import useDocSpaces from './useDocSpaces';
import { MAIN_KEY, bucketScopeFor, docSpaceChoices, sectionIdOf, spaceKey } from './docSpaceChoices';
import { resolveChoice } from './scopeChoices';
import { docNodeId } from './documentsProjectFilter';
import { moveNodeToSpace, orderSpaces } from '../../scope/docSpaceActions';

const warn = (err) => console.warn('[project-scope] space change failed:', err);

export default function useSpaceHeader({ t, bucketChoice }) {
  const docSpaces = useDocSpaces();
  const [dialog, setDialog] = useState(null); // 'new' | 'manage' | null
  const { project, spaces, selectedSpaceId, select, members } = docSpaces;

  const bucketScope = useMemo(() => bucketScopeFor(project, bucketChoice?.kind === 'device' ? bucketChoice.scope : { type: 'personal' }), [project, bucketChoice]);
  const choices = useMemo(() => (project ? docSpaceChoices({ t, spaces, bucketScope }) : []), [project, t, spaces, bucketScope]);
  const current = useMemo(() => (project ? resolveChoice(choices, spaceKey(selectedSpaceId)) : null), [project, choices, selectedSpaceId]);

  const onPick = useCallback((key) => select(key === MAIN_KEY ? null : String(key).slice('space:'.length)), [select]);

  // A tree row dropped onto Main / a space: only a page that is itself in the
  // project moves (a page inside a folder follows its folder).
  const onMoveTree = useCallback(
    (drag, choice) => {
      if (!project || !drag?.node) return;
      moveNodeToSpace(project, docNodeId(drag.node), choice.scopeId || null).catch(warn);
    },
    [project]
  );
  const onReorder = useCallback((ids) => project && orderSpaces(project, spaces, ids).catch(warn), [project, spaces]);

  return {
    inProject: Boolean(project),
    choices,
    current: current ? { ...current, sectionId: sectionIdOf(bucketScope) } : null,
    onPick,
    onMoveTree,
    onReorder,
    onNew: () => setDialog('new'),
    onManage: () => setDialog('manage'),
    dialog,
    setDialog,
    docSpaces: { project, spaces, members },
  };
}
